import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';

import api from '../../../services/api';
import { serializeFilters, toAnalyticsParams } from '../../../components/performance/pmAnalyticsFilters';
import { hasValue, round2 } from '../../performance-operations/config';

// PM analitik P2 — E01 Yönetim Panosu, E03 Performans Sonuçları ve E10 Organizasyon Karşılaştırmaları'nın ORTAK veri katmanı.
// Uçlar: GET v1/performance-results/{distribution|components|employees|units|units/detail} (+ export=xlsx) ve KPI'lar için mevcut
// GET v1/performance-analytics/metrics (E01 ile E03 AYNI uçtan aynı kodları okur → M06 için aynı değer ve n).
// api.js GET'te reject ETMEZ — hata sinyali res.data.status (false/eksik → panel hata durumu; "boş" ayrı durum).

export const RESULTS_PATH = '/performance/results';
export const EXECUTIVE_PATH = '/performance/executive';
export const ORG_COMPARISON_PATH = '/performance/org-comparison';
// E14 çalışan profili: /performance/profile/:employeeId — dönem ?periodId= ile taşınır (P7a sayfasının sözleşmesi).
export const PROFILE_PATH = '/performance/profile';
// E11 dönemler arası trendler (başka paket). Rota kesinleşince buraya yazılır; null iken bağlantı gösterilmez.
export const TRENDS_PATH = '/performance/trends';

export const EXECUTIVE_KPI_CODES = ['M01', 'M02', 'M06', 'M09', 'M07', 'M08'];
export const RESULT_KPI_CODES = ['M06', 'M07', 'M08', 'M51'];
export const UNIT_METRIC_CODES = ['M01', 'M02', 'M06', 'M07', 'M08', 'M09', 'M51'];
export const DEFAULT_HEATMAP_CODES = ['M02', 'M06', 'M09', 'M07', 'M08'];
export const SUPPRESSED_REASON = 'ha.suppressed.belowThreshold';
export const BANDS = ['low', 'mid', 'high'];
export const GOAL_BANDS = ['on_track', 'watch', 'at_risk'];
// grade / jobFamily: ünvandan türetilir (spec 2026-10-27 §7.6); birim tablosu zaten yalnız İK.
export const GROUP_BYS = ['hierarchy', 'department', 'location', 'title', 'grade', 'jobFamily'];
export const MIN_N_OPTIONS = [0, 5, 10, 20, 50];

// Metrik yönü: artış iyi mi (renk/ok yönü). M01 nötrdür; düşük performans oranında artış kötüdür.
export const HIGHER_IS_BETTER = {
  M01: null, M02: true, M06: true, M07: true, M08: false, M09: true, M51: true, M60: true,
};

// Sayfa düzeyi URL anahtarları (ortak süzgeç anahtarlarıyla çakışmaz; serializeFilters bunları korur).
export const Q = {
  prelim: 'prelim',
  band: 'band',
  bucket: 'bucket',
  goalBand: 'gband',
  calibrationChanged: 'calib',
  published: 'pub',
  missing: 'missing',
  groupBy: 'grp',
  parent: 'parent',
  metrics: 'metrics',
  minN: 'minn',
  focus: 'focus',
};

const payload = (res) => (res?.data?.status ? res.data.data : undefined);

export class ResultsPanelError extends Error {}

async function get(url, params) {
  const res = await api.get(url, { params });
  const data = payload(res);
  if (data === undefined) throw new ResultsPanelError(res?.data?.message || 'error');
  return data;
}

const scoreParams = (prelim) => (prelim ? { scoreState: 'preliminary' } : {});

export const fetchDistribution = (filters, periodId, { prelim = false } = {}) => get('/v1/performance-results/distribution', {
  ...toAnalyticsParams(filters, periodId), ...scoreParams(prelim),
});

export const fetchComponents = (filters, { prelim = false } = {}) => get('/v1/performance-results/components', {
  ...toAnalyticsParams(filters), ...scoreParams(prelim),
});

// Liste süzgeçleri (URL'deki sayfa anahtarlarından) → uç parametreleri. Bant/kova/hedef bandı tek değer taşır (grafikten iniş).
export function listParams(page) {
  const params = {};
  if (page.band) params['band[in]'] = page.band;
  if (page.bucket) params['bucket[in]'] = page.bucket;
  if (page.goalBand) params['goalBand[in]'] = page.goalBand;
  if (page.calibrationChanged) params.calibrationChanged = page.calibrationChanged;
  if (page.published) params.published = page.published;
  if (page.missing) params.missing = page.missing;
  return params;
}

export const fetchEmployees = (filters, {
  pageNumber = 1, pageSize = 10, list = {}, sort, order, comparePeriodId, prelim = false,
} = {}) => get('/v1/performance-results/employees', {
  ...toAnalyticsParams(filters), ...scoreParams(prelim), ...list,
  ...(sort ? { sort, order: order || 'asc' } : {}),
  ...(comparePeriodId ? { comparePeriodId } : {}),
  pageNumber, pageSize,
});

export const exportEmployees = (filters, { list = {}, sort, order, comparePeriodId, prelim = false } = {}) => api.get('/v1/performance-results/employees', {
  params: {
    ...toAnalyticsParams(filters), ...scoreParams(prelim), ...list,
    ...(sort ? { sort, order: order || 'asc' } : {}),
    ...(comparePeriodId ? { comparePeriodId } : {}),
    export: 'xlsx',
  },
  responseType: 'blob',
});

const unitParams = ({ groupBy, parentEmployeeId, minN, comparePeriodId }) => ({
  ...(groupBy ? { groupBy } : {}),
  ...(parentEmployeeId ? { parentEmployeeId } : {}),
  ...(minN ? { minN } : {}),
  ...(comparePeriodId ? { comparePeriodId } : {}),
});

export const fetchUnits = (filters, options = {}) => get('/v1/performance-results/units', {
  ...toAnalyticsParams(filters), ...unitParams(options),
});

export const exportUnits = (filters, options = {}) => api.get('/v1/performance-results/units', {
  params: { ...toAnalyticsParams(filters), ...unitParams(options), export: 'xlsx' },
  responseType: 'blob',
});

export const fetchUnitDetail = (filters, { groupBy, parentEmployeeId, unitKey }) => get('/v1/performance-results/units/detail', {
  ...toAnalyticsParams(filters),
  ...(groupBy ? { groupBy } : {}),
  ...(parentEmployeeId ? { parentEmployeeId } : {}),
  unitKey,
});

// Excel indirme: başarısız GET'te interceptor hatayı zaten tostlar ve başlıksız {status:false} döner — sessizce çıkılır.
export function downloadBlob(res, fallbackName) {
  if (!res?.headers || res.data?.status === false) return false;
  const blob = new Blob([res.data], { type: res.headers['content-type'] });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const match = res.headers['content-disposition']?.match(/filename[^;=\n]*=([^;\n]*)/);
  a.download = match ? match[1].replaceAll('"', '') : fallbackName;
  a.click();
  URL.revokeObjectURL(url);
  return true;
}

// ---------------------------------------------------------------- URL / bağlantılar
function withParams(path, filters, extra = {}) {
  const p = new URLSearchParams(serializeFilters(filters || {}).replace(/^\?/, ''));
  Object.entries(extra).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') p.delete(k);
    else p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `${path}?${s}` : path;
}

export const resultsLink = (filters, extra) => withParams(RESULTS_PATH, filters, extra);
export const orgComparisonLink = (filters, extra) => withParams(ORG_COMPARISON_PATH, filters, extra);
export const profileLink = (employeeId, periodId) => `${PROFILE_PATH}/${employeeId}${periodId ? `?periodId=${periodId}` : ''}`;
export const trendsLink = (filters) => (TRENDS_PATH ? withParams(TRENDS_PATH, filters) : null);

/**
 * Sayfa düzeyi URL durumu (ortak süzgeç dışındaki anahtarlar): geri dönüşte ve yenilemede korunur. `set` verilen anahtarları
 * yazar (null/'' → siler), diğer anahtarlara (ortak süzgeç dahil) dokunmaz.
 */
export function usePageParams(keys) {
  const location = useLocation();
  const history = useHistory();
  const keyStr = keys.join(',');
  const values = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return Object.fromEntries(keyStr.split(',').map((k) => [k, p.get(k)]));
  }, [location.search, keyStr]);
  const set = useCallback((patch, { push = false } = {}) => {
    const p = new URLSearchParams(location.search);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '' || v === false) p.delete(k);
      else p.set(k, String(v));
    });
    const s = p.toString();
    const next = { pathname: location.pathname, search: s ? `?${s}` : '' };
    if (push) history.push(next); else history.replace(next);
  }, [history, location.pathname, location.search]);
  return [values, set];
}

// ---------------------------------------------------------------- değer yardımcıları
export const isSuppressed = (m) => !!m?.suppressionReason;

export const metricByCode = (list, code) => (list || []).find((m) => m.metricCode === code) || null;

/**
 * KPI farkı (karşılaştırma dönemine göre): yüzdelerde yüzde puan, puanlarda puan farkı, kapsamda göreli %.
 * Ortalama puan (M06) yalnız AYNI ölçekte kıyaslanır; gizli hücre fark üretmez.
 */
export function resultDiff(code, current, comparison) {
  if (!comparison) return { reason: 'noComparePeriod' };
  if (isSuppressed(current) || isSuppressed(comparison)) return { reason: 'suppressed' };
  if (!hasValue(current) || !hasValue(comparison)) return { reason: 'noData' };
  if (code === 'M06' && (current.scaleId || null) !== (comparison.scaleId || null)) return { reason: 'scale' };
  if (code === 'M01') {
    const prev = Number(comparison.value);
    if (prev === 0) return { reason: 'noData' };
    return { delta: round2(((Number(current.value) - prev) / prev) * 100), relative: true };
  }
  return { delta: round2(Number(current.value) - Number(comparison.value)) };
}

export const diffTone = (code, delta) => {
  const higher = HIGHER_IS_BETTER[code];
  if (delta == null || delta === 0 || higher === null || higher === undefined) return 'neutral';
  return (delta > 0) === higher ? 'good' : 'bad';
};

export const formatNum = (v, lang, digits = 2) => (v === null || v === undefined ? '' : new Intl.NumberFormat(lang, { maximumFractionDigits: digits }).format(Number(v)));

/** Puan "3,74 / 5" biçiminde (spec §4.2); yalnız hedef sisteminde "/ 100". */
export const formatScore = (v, scaleMax, lang) => (v === null || v === undefined ? '' : `${formatNum(v, lang)}${scaleMax ? ` / ${formatNum(scaleMax, lang, 0)}` : ''}`);
export const formatPct = (v, lang) => (v === null || v === undefined ? '' : `%${formatNum(v, lang)}`);

/** Metrik hücresinin metni (gizli → "Gizli", veri yok → "Veri Yok"; sıfır yalnız gerçek sıfırda). */
export function metricText(t, m, lang) {
  if (!m) return t('performance.results.analytics.noData');
  if (isSuppressed(m)) return t('performance.results.analytics.suppressed');
  if (!hasValue(m)) return t('performance.results.analytics.noData');
  if (m.unit === 'percent') return formatPct(m.value, lang);
  if (m.unit === 'score') return formatScore(m.value, m.scaleMax, lang);
  return formatNum(m.value, lang, 0);
}

// Bant ve hedef bandı renkleri — tema token'ı; renk her zaman ad + sayı ile birlikte (yalnız renkle anlam yok).
export function useBandColors() {
  const theme = useTheme();
  return useMemo(() => ({
    low: theme.colors.auxiliar.danger,
    mid: theme.colors.primary.main,
    high: theme.colors.secondary.dark,
    none: theme.colors.text.light.medium,
    on_track: theme.colors.secondary.dark,
    watch: theme.colors.auxiliar.warning,
    at_risk: theme.colors.auxiliar.danger,
    compare: theme.colors.auxiliar.purple,
    goal: theme.colors.primary.main,
    competency: theme.colors.secondary.dark,
    combined: theme.colors.auxiliar.purple,
  }), [theme]);
}

// Dağılım kovasının adı: seviyede ölçek etiketi, aralıkta "40–60", bantta çeviri.
export function bucketLabel(t, bucket, kind, lang) {
  if (kind === 'bands') return t(`performance.results.analytics.band.${bucket.key}`);
  if (kind === 'goalBands') return t(`performance.results.analytics.goalBand.${bucket.key}`);
  if (bucket.label) return bucket.label;
  if (bucket.to === null || bucket.to === undefined) return `${formatNum(bucket.from, lang, 0)}+`;
  return `${formatNum(bucket.from, lang, 0)}–${formatNum(bucket.to, lang, 0)}`;
}

/**
 * Dağılımın iki dönemlik satırları. Karşılaştırma yalnız aynı ölçekte (seviye kovaları için ölçek kimliği aynı, eşleşme
 * sayısal değerle); bant ve hedef bandı her zaman kıyaslanabilir. Değerler yüzde (farklı n'li dönemler kıyaslanabilsin).
 */
export function distributionRows(current, compare, kind) {
  const list = (d) => (kind === 'bands' ? d?.bands : kind === 'goalBands' ? d?.goalBands : d?.levels) || [];
  const comparable = !!compare && (kind !== 'levels'
    || (current?.scaleMode === compare.scaleMode && (!current?.scaleMode || current?.scaleId === compare.scaleId)));
  const keyOf = (b) => (kind === 'levels' && current?.scaleMode ? String(b.numericValue) : b.key);
  const prev = comparable ? new Map(list(compare).map((b) => [keyOf(b), b])) : new Map();
  return {
    comparable,
    rows: list(current).map((b) => {
      const p = prev.get(keyOf(b));
      return {
        key: b.key,
        bucket: b,
        band: kind === 'bands' ? b.key : (kind === 'goalBands' ? b.key : (b.band || 'none')),
        count: b.count,
        pct: b.pct,
        compareCount: p ? p.count : null,
        comparePct: p ? p.pct : null,
      };
    }),
  };
}

// Birim adı: hiyerarşide "X'in Ekibi", üst yöneticinin doğrudan ekibi, zinciri olmayanlar ve boyutu boş olanlar ayrı adlanır.
export function unitLabel(t, unit) {
  if (!unit) return '';
  if (unit.kind === 'direct') return t('performance.results.analytics.unit.direct', { name: unit.managerName || '' });
  if (unit.kind === 'root') return t('performance.results.analytics.unit.root');
  if (unit.kind === 'unassigned') return t('performance.results.analytics.unit.unassigned');
  if (unit.kind === 'company') return t('performance.results.analytics.unit.company');
  if (unit.kind === 'manager') return t('performance.results.analytics.unit.team', { name: unit.label || unit.managerName || '' });
  return unit.label || t('performance.results.analytics.unit.unknown');
}

/** Kritik birim nedeni: "Tamamlanma şirketin 12,5 puan altında" (açık eşik; anlamlılık ifadesi kullanılmaz). */
export function reasonText(t, reason, lang) {
  return t(`performance.results.analytics.critical.reason.${reason.metricCode}`, { gap: formatNum(Math.abs(Number(reason.gap)), lang) });
}

// Panel meta (şeritteki "Son Güncelleme" ve "Eski Veri").
export const metaOf = (d) => ({ calculatedAt: d?.calculatedAt || null, stale: !!d?.stale });
